import React from 'react';
import PivotTableSummary from './PivotTableSummary';
import { ServiceAttributeSummaryData } from '../../models/PulseData';
import ServiceAttributeSummary, { getServiceAttributeContentId } from './ServiceAttributeSummary';
import { FetchState } from '../../models/commonTypes';
import PulseContentHeader from './PulseContentHeader';
import PulseContentFooter from './PulseContentFooter';
import { copyToClipboard, copyImageToClipboard } from '../../utils/clipboardUtils';
import { useNotifier } from '../../hooks/useNotifier';
import { toBlob } from 'html-to-image';
import logger from '../../utils/logger';
import { openPrintView } from './printUtils';
import { SummaryType } from './PulseNav';
import { ServiceDetailViewRenderPayload } from './PulseContent';

export const SERVICE_DETAIL_VIEW_CONTENT_ID = 'service-detail-view-content-area';

interface ServiceDetailViewProps extends ServiceDetailViewRenderPayload {
  // Common props
  servicesGroup: string; // May not be used directly but passed for consistency
  currentStartDate: Date;
  currentEndDate: Date;
  mainCssUrl: string;
  isLoadingOverall: boolean;
  activeSummary: SummaryType;
}

// Helper to determine if any specific attribute is loading for this view
const isServiceDetailLoading = (isLoadingAttrs: Record<string, boolean>, pivotState: FetchState<any>) => {
  return Object.values(isLoadingAttrs).some(Boolean) || pivotState.isLoading;
};

const ServiceDetailView: React.FC<ServiceDetailViewProps> = ({
  serviceName,
  keyAttributes,
  selectedAttributeForServicePivot,
  serviceAttributePivotState,
  isLoadingAttributes,
  attributeErrors,
  serviceAttributesData,
  // Common props
  currentStartDate,
  currentEndDate,
  mainCssUrl,
  isLoadingOverall, // Could use more specific loading below
  // activeSummary, // serviceName is more specific here
}) => {
  const { showNotification } = useNotifier();

  const handleCopyRawData = async () => {
    const payload = getServiceDetailRawDataPayload(serviceAttributesData, serviceName);
    if (payload) {
      await copyToClipboard(JSON.stringify(payload.data, null, 2), showNotification, payload.message);
    } else {
      showNotification('No raw data for service detail.', 'warning');
    }
  };

  const handleCopyView = async () => {
    // For service detail, we copy the whole view container
    const viewCopyInfo = { elementId: SERVICE_DETAIL_VIEW_CONTENT_ID, successMessage: `View for service ${serviceName} copied!` };
    const elementToCapture = document.getElementById(viewCopyInfo.elementId);
    if (elementToCapture) {
      try {
        const imageBlob = await toBlob(elementToCapture, { backgroundColor: 'white' });
        if (imageBlob) await copyImageToClipboard(imageBlob, showNotification, viewCopyInfo.successMessage);
        else throw new Error('Failed to generate blob.');
      } catch (err) {
        logger.error(`Error capturing service ${serviceName} view:`, err);
        showNotification('Failed to copy service view as image.', 'error');
      }
    } else {
      showNotification(`Element '${viewCopyInfo.elementId}' not found.`, 'warning');
    }
  };

  const handlePrintView = async () => {
    const printInfo = getServiceDetailPrintMetadata(serviceName); // Uses SERVICE_DETAIL_VIEW_CONTENT_ID
    const elementToCapture = document.getElementById(printInfo.elementId);
    if (elementToCapture) {
      openPrintView({ elementToCapture, jobTitle: printInfo.jobTitle, mainCssUrl, headerContent: printInfo.headerContent, showNotification });
    } else {
      showNotification('Could not find service detail content to print.', 'warning');
    }
  };

  const title = `Service: ${serviceName}`;
  const specificIsLoading = isServiceDetailLoading(isLoadingAttributes, serviceAttributePivotState);

  return (
    <div id={SERVICE_DETAIL_VIEW_CONTENT_ID}>
      <PulseContentHeader
        title={title}
        isLoading={specificIsLoading} // Use specific loading state
        onCopyRawData={handleCopyRawData}
        onCopyView={handleCopyView}
        onPrintView={handlePrintView}
      />
      <div className="service-detail-content-body"> {/* Optional inner wrapper if needed */}

        {/* 
          Display the selected attribute pivot table 
          NOTE: for some customers, there may be NO attribute to pivot data per service.
          in that case, we silently ignore this block
        */}
        {selectedAttributeForServicePivot &&

          <div className="service-attribute-item" id={getServiceAttributeContentId(serviceName, 'pivot_msg')}>
            { serviceAttributePivotState.isLoading && (
              <div style={{ marginTop: 'var(--spacing-large)' }}><p>Loading pivot table for <strong>{selectedAttributeForServicePivot}</strong> attribute...</p></div>
            )}

            { serviceAttributePivotState.error && (
              <div className="error-banner" style={{ marginTop: 'var(--spacing-large)' }}><p>Error loading pivot table for {selectedAttributeForServicePivot} attribute: {serviceAttributePivotState.error}</p></div>
            )}

            { serviceAttributePivotState.data && !serviceAttributePivotState.isLoading && !serviceAttributePivotState.error && (
              <div style={{ marginTop: 'var(--spacing-large)' }}><PivotTableSummary data={serviceAttributePivotState.data} title={`Pivot: '${selectedAttributeForServicePivot}' for ${serviceName}`} /></div>
            )}
          </div>
        }

        {keyAttributes.map((attribute, index) => {
          const isLoading = isLoadingAttributes[attribute];
          const error = attributeErrors[attribute];
          const data = serviceAttributesData[attribute];

          if (isLoading) return <div key={attribute}><p>Loading summary for <strong>{attribute}</strong>...</p></div>;
          if (error) return <div key={attribute} className="error-banner" style={{ marginBottom: 'var(--spacing-medium)' }}><p>Error loading summary for <strong>{attribute}</strong>: {error}</p></div>;

          if (data) return (
            <div key={attribute} style={index > 0 ? { marginTop: 'var(--spacing-large)' } : {}}>
                <ServiceAttributeSummary data={data} />
            </div>
          );

          return (
          <div className="service-attribute-item" id={getServiceAttributeContentId(serviceName, attribute)}>
            <h3>Attribute: {attribute}</h3>
            <div key={attribute}><p>No summary data for <strong>{attribute}</strong>.</p></div>
          </div>
          );
        })}
      </div>
      <PulseContentFooter currentStartDate={currentStartDate} currentEndDate={currentEndDate} />
    </div>
  );
};

export const getServiceDetailRawDataPayload = (
  serviceAttributesData: Record<string, ServiceAttributeSummaryData | null>,
  serviceName: string
): { data: any; message: string } | null => {
  if (!serviceAttributesData) return null;
  return {
    data: serviceAttributesData,
    message: `Attribute data for service ${serviceName} copied to clipboard!`,
  };
};

export const getServiceDetailViewCopyInfo = (
  serviceAttributesData: Record<string, ServiceAttributeSummaryData | null>,
  serviceName: string
): { elementId: string; successMessage: string } | null => {
  const firstAttributeKey = Object.keys(serviceAttributesData).find(key => serviceAttributesData[key]);
  if (firstAttributeKey && serviceAttributesData[firstAttributeKey]) {
    const attributeData = serviceAttributesData[firstAttributeKey]!;
    return {
      elementId: getServiceAttributeContentId(attributeData.services, attributeData.attribute),
      successMessage: `View for ${attributeData.attribute} of service ${serviceName} copied!`,
    };
  }
  return null;
};

export const getServiceDetailPrintMetadata = (serviceName: string) => {
  return {
    elementId: SERVICE_DETAIL_VIEW_CONTENT_ID, // Use the local ID
    jobTitle: `Attributes for [${serviceName}] - ${new Date().toLocaleDateString()}`,
    headerContent: `Service: ${serviceName}`,
  };
};

export default ServiceDetailView;